import React, { useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import { tokens } from '../../../theme/tokens';
import { StatusChip } from '../../ui';
import type { ProductMatchResult } from '../../../services/integrationApi';
import type { ListingHold } from './useListingMatchesToConfirm';

export const REASON_LABEL: Record<string, string> = {
  barcode_on_other_sku: 'Barcode on another SKU',
  identity_review: 'Needs identity check',
  archived_listing: 'Archived listing',
  title: 'Similar title',
  handle: 'Same handle',
};

/** The attribute the two boxes share, shown between them. */
export const REASON_SHARED: Record<string, string> = {
  barcode_on_other_sku: 'same barcode',
  identity_review: 'same SKU or barcode',
  archived_listing: 'same SKU or barcode',
  title: 'similar title',
  handle: 'same handle',
};

export type HoldDecision = 'update' | 'create';

export interface ResolvedHold {
  hold: ListingHold;
  decision: HoldDecision;
  /** Title of the Shopify listing the sync will update (decision `update`). */
  listingTitle: string | null;
  /** Still inside the undo window: the server has not been told yet. */
  undoable: boolean;
}

interface Side {
  name: string;
  sku?: string | null;
  barcode?: string | null;
}

const identifiers = (side: Side): string =>
  [side.sku ? `SKU ${side.sku}` : null, side.barcode ? `Barcode ${side.barcode}` : null].filter(Boolean).join(' · ');

const CompareBox: React.FC<{ label: string; side: Side }> = ({ label, side }) => (
  <Box
    sx={{
      p: '10px 12px',
      border: `1px solid ${tokens.line.default}`,
      borderRadius: `${tokens.radius.control}px`,
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.faint }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{side.name}</Typography>
    {identifiers(side) && (
      <Typography sx={{ fontSize: 12, color: tokens.ink.muted, overflowWrap: 'anywhere' }}>{identifiers(side)}</Typography>
    )}
  </Box>
);

interface ScanLookup {
  shopify: Map<string, NonNullable<ProductMatchResult['unmatchedShopify']>[number]>;
  motzuBySku: Map<string, NonNullable<ProductMatchResult['unmatchedMotzu']>[number]>;
}

/** SKU and barcode are not on a hold; the last scan's unmatched lists carry them when the listing was in it. */
export const lookupFromScan = (result: ProductMatchResult | null): ScanLookup => ({
  shopify: new Map((result?.unmatchedShopify ?? []).map((l) => [l.shopifyProductId, l])),
  motzuBySku: new Map((result?.unmatchedMotzu ?? []).filter((p) => p.sku).map((p) => [String(p.sku), p])),
});

interface OpenProps {
  hold: ListingHold;
  lookup: ScanLookup;
  busy: boolean;
  onDecide: (decision: HoldDecision, candidate: { productId: string; title: string } | null) => void;
}

/** One unresolved item: the two listings side by side and the two decisions. */
export const ListingHoldOpenItem: React.FC<OpenProps> = ({ hold, lookup, busy, onDecide }) => {
  const candidates = hold.candidates.filter((c) => c.productId);
  const [chosenId, setChosenId] = useState<string>(candidates[0]?.productId ?? '');
  const chosen = candidates.find((c) => c.productId === chosenId) ?? candidates[0];
  const listing = chosen ? lookup.shopify.get(chosen.productId) : undefined;
  const motzu = hold.productSku ? lookup.motzuBySku.get(hold.productSku) : undefined;
  const shared = REASON_SHARED[hold.reason];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', p: '16px 20px', borderTop: `1px solid ${tokens.line.hairline}` }} data-testid="listing-hold">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{hold.productName || hold.productSku || hold.productId}</Typography>
        {hold.productSku && (
          <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 12, color: tokens.ink.muted }}>{hold.productSku}</Typography>
        )}
        <StatusChip label={REASON_LABEL[hold.reason] || 'Needs confirmation'} tone="caution" size="small" />
      </Box>
      {chosen ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(0,1fr) auto minmax(0,1fr)' }, gap: '12px', alignItems: 'center' }}>
          <CompareBox
            label="In Motzu"
            side={{ name: hold.productName || hold.productSku || hold.productId, sku: hold.productSku, barcode: motzu?.upc }}
          />
          {shared && (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.signal.caution.ink, textAlign: 'center' }}>{shared}</Typography>
          )}
          <CompareBox label="In Shopify" side={{ name: chosen.title, sku: listing?.sku, barcode: listing?.barcode }} />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">{hold.message}</Typography>
      )}
      {candidates.length > 1 && (
        <TextField
          select
          size="small"
          label="Possible listing"
          value={chosen?.productId ?? ''}
          onChange={(e) => setChosenId(e.target.value)}
          sx={{ maxWidth: 420 }}
        >
          {candidates.map((c) => (
            <MenuItem key={c.productId} value={c.productId}>
              {c.title}
              {c.status ? ` (${c.status.toLowerCase()})` : ''}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        {chosen && (
          <Button variant="contained" size="small" disabled={busy} onClick={() => onDecide('update', chosen)}>
            Update the Shopify listing
          </Button>
        )}
        <Button variant="outlined" color="inherit" size="small" disabled={busy} onClick={() => onDecide('create', null)}>
          Create a new listing
        </Button>
      </Box>
    </Box>
  );
};

/** The resolved state: a green check, the outcome in one line, and Undo while it can still be taken back. */
export const ListingHoldResolvedItem: React.FC<{ item: ResolvedHold; onUndo: () => void }> = ({ item, onUndo }) => {
  const label = item.hold.productName || item.hold.productSku || item.hold.productId;
  const text =
    item.decision === 'update'
      ? `${label}: the next sync will update "${item.listingTitle ?? 'that listing'}".`
      : `${label}: the next sync may create a new listing.`;
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', p: '16px 20px', borderTop: `1px solid ${tokens.line.hairline}` }}
      data-testid="listing-hold-resolved"
    >
      <Check sx={{ fontSize: 16, color: tokens.signal.positive.ink }} />
      <Typography sx={{ flex: '1 1 auto', fontSize: 13, color: tokens.ink.body }}>{text}</Typography>
      {item.undoable && (
        <Button size="small" variant="text" onClick={onUndo} sx={{ fontWeight: 600 }}>
          Undo
        </Button>
      )}
    </Box>
  );
};
